import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Composants chargés immédiatement (layout, utilisés partout)
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import CookieConsent from './components/CookieConsent';
import ProtectedAdminRoute from './components/ProtectedAdminRoute';
import { LanguageProvider } from './context/LanguageContext';
import { UserProfileProvider } from './context/UserProfileContext';
import { useAuth } from './hooks/useAuth';

// Lazy loading des pages pour un chargement initial plus rapide
const Home = lazy(() => import('./pages/Home'));
const Profile = lazy(() => import('./pages/Profile'));
const Contact = lazy(() => import('./pages/Contact'));
const About = lazy(() => import('./pages/About'));
const Services = lazy(() => import('./pages/Services'));
const ProviderRegistration = lazy(() => import('./pages/ProviderRegistration'));
const UserRegistration = lazy(() => import('./pages/UserRegistration'));
const ProviderRegistrationPage = lazy(() => import('./pages/ProviderRegistrationPage'));
const MessagingPage = lazy(() => import('./pages/MessagingPage'));
const Admin = lazy(() => import('./pages/Admin'));
const Tests = lazy(() => import('./pages/Tests'));
const Options = lazy(() => import('./pages/Options'));
const Users = lazy(() => import('./pages/Users'));
const ForumPage = lazy(() => import('./pages/ForumPage'));
const TopicDetailPage = lazy(() => import('./pages/TopicDetailPage'));
const CreateTopicPage = lazy(() => import('./pages/CreateTopicPage'));
const AdminForumPage = lazy(() => import('./pages/AdminForumPage'));
const MentionsLegales = lazy(() => import('./pages/MentionsLegales'));
const CGU = lazy(() => import('./pages/CGU'));
const CharteReferencement = lazy(() => import('./pages/CharteReferencement'));

/** Remet le scroll en haut de la page à chaque changement de route */
const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    const main = document.querySelector('main');
    if (main) (main as HTMLElement).scrollTop = 0;
  }, [pathname]);

  return null;
};

/** Routes pour lesquelles le footer doit rester collé en bas (sticky) */
const REGISTRATION_PATHS = ['/register-user', '/register-provider'];

/** Layout principal (doit être enfant de Router pour useLocation) */
const AppLayout: React.FC = () => {
  const { pathname } = useLocation();
  const { user, loading, login } = useAuth();
  const isRegistrationLayout = REGISTRATION_PATHS.includes(pathname);

  React.useEffect(() => {
    const handleContextMenu = (event: MouseEvent) => {
      event.preventDefault();
    };
    document.addEventListener('contextmenu', handleContextMenu);
    return () => document.removeEventListener('contextmenu', handleContextMenu);
  }, []);

  React.useEffect(() => {
    const root = document.getElementById('root');
    if (!root) return;
    if (isRegistrationLayout) {
      root.classList.add('registration-layout-root');
    } else {
      root.classList.remove('registration-layout-root');
    }
    return () => root.classList.remove('registration-layout-root');
  }, [isRegistrationLayout]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  const appStyle: React.CSSProperties = {
    minHeight: 'calc(100vh - var(--navbar-height, 72px))',
    ...(isRegistrationLayout ? { display: 'flex', flexDirection: 'column' } : {}),
  };
  const mainStyle: React.CSSProperties = isRegistrationLayout
    ? { flex: '1 1 auto', display: 'flex', flexDirection: 'column' }
    : {};

  return (
    <>
      <ScrollToTop />
      <div
        className={`App d-flex flex-column ${isRegistrationLayout ? 'app-registration-layout' : ''}`}
        style={appStyle}
      >
        <Navbar user={user} setUser={login} />
        <main className="flex-grow-1" style={mainStyle}>
          {isRegistrationLayout ? (
            <div className="registration-main-inner" style={{ flex: '0 1 auto', display: 'flex', flexDirection: 'column' }}>
              <Suspense fallback={
                <div className="d-flex justify-content-center align-items-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Chargement...</span>
                  </div>
                </div>
              }>
                <Routes>
                  <Route path="/register-user" element={<UserRegistration />} />
                  <Route path="/register-provider" element={<ProviderRegistrationPage />} />
                </Routes>
              </Suspense>
            </div>
          ) : (
            <Suspense fallback={
              <div className="d-flex justify-content-center align-items-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Chargement...</span>
                </div>
              </div>
            }>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/profil" element={<Profile />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/about" element={<About />} />
                <Route path="/services" element={<Services />} />
                <Route path="/register" element={<ProviderRegistration />} />
                <Route path="/register-user" element={<UserRegistration />} />
                <Route path="/register-provider" element={<ProviderRegistrationPage />} />
                <Route path="/messaging" element={<MessagingPage />} />
                <Route path="/forum" element={<ForumPage />} />
                <Route path="/forum/topic/:topicId" element={<TopicDetailPage />} />
                <Route path="/forum/create" element={<CreateTopicPage />} />
                <Route path="/admin" element={<ProtectedAdminRoute><Admin /></ProtectedAdminRoute>} />
                <Route path="/admin/forum" element={<ProtectedAdminRoute><AdminForumPage /></ProtectedAdminRoute>} />
                <Route path="/tests" element={<Tests />} />
                <Route path="/options" element={<Options />} />
                <Route path="/users" element={<Users />} />
                <Route path="/mentions-legales" element={<MentionsLegales />} />
                <Route path="/cgu" element={<CGU />} />
                <Route path="/charte-referencement" element={<CharteReferencement />} />
              </Routes>
            </Suspense>
          )}
        </main>
        {isRegistrationLayout && <div className="registration-footer-spacer" aria-hidden="true" />}
        <Footer />
        <CookieConsent />
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </div>
    </>
  );
};

const App: React.FC = () => (
  <LanguageProvider>
    <UserProfileProvider>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AppLayout />
      </Router>
    </UserProfileProvider>
  </LanguageProvider>
);

export default App;
